<template>
  <section>
    <b-button
      v-if="accountAccessManager.canManageProperties()"
      variant="primary"
      class="mb-2"
      size="sm"
      @click="createProperty"
    >
      <GjIcon
        class="mr-50"
        name="Plus"
      />
      {{ $tk('ClientPortal.CreateProperty') }}
    </b-button>
    <b-skeleton-table
      v-if="loading"
      :rows="10"
      :columns="5"
      :table-props="{ bordered: true, striped: true }"
    />
    <b-card
      v-else
      no-body
      class="client-table"
    >
      <b-table
        id="properties-table"
        :items="properties"
        :fields="fields"
        class="client-table__data"
        show-empty
        :empty-text="$tk('ClientPortal.Properties.NoPropertiesHaveBeenCreatedYet')"
        :current-page="currentPage"
        :per-page="perPage"
        responsive
      >
        <template #cell(name)="data">
          <b-link :to=" '/' +$route.params.accountId +'/property/details?propertyId='+data.item.id">
            {{ data.item.name }}
          </b-link>
        </template>
        <template #empty="scope">
          <div class="text-center p-3 client-table__empty">
            <div class="empty__client-table">
              <GjIcon
                size="64px"
                name="Widget"
              />
              <div class="mb-50">
                {{ scope.emptyText }}
              </div>
              <b-link @click="createProperty">
                <GjIcon
                  size="14"
                  name="Plus"
                />
                <span class="create-text">{{ $tk('ClientPortal.CreateProperty') }}</span>
              </b-link>
            </div>
          </div>
        </template>
        <template #cell(status)="data">
          <div class="d-flex align-items-center justify-content-between">
            <span>
              {{ data.item.status }}
            </span>
            <b-dropdown
              v-if="accountAccessManager.canManageProperties()"
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              button-class="p-0"
              right
            >
              <template v-slot:button-content>
                <GjIcon name="Moreoptions_fill" />
              </template>
              <b-dropdown-item @click="editProperty(data.item)">
                <GjIcon
                  class="mr-50"
                  name="Edit"
                />
                {{ $tk('ClientPortal.Property.Edit') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>
      <b-card-footer
        v-if="properties.length > 0"
        class="client-table__footer d-flex"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
          class="my-0 mr-1"
          hide-goto-end-buttons
        >
          <template #prev-text>
            <GjIcon
              class="mr-25"
              name="ArrowLeft"
            />
          </template>
          <template #next-text>
            <GjIcon
              class="ml-25"
              name="ArrowRight"
            />
          </template>
        </b-pagination>
        <span>
          {{ $tk('ClientPortal.Pagination.Viewing') }} {{ fromCount }} {{ $tk('ClientPortal.Pagination.To') }} {{ toCount }} {{ $tk('ClientPortal.Pagination.Of') }} {{ totalRows }} {{ $tk('ClientPortal.Pagination.Items') }}
        </span>
      </b-card-footer>
    </b-card>
    <b-modal
      id="property-modal"
      ref="property-modal"
      centered
      :title="model.id !== undefined ? $tk('ClientPortal.Form.EditProperty') : $tk('ClientPortal.Form.CreateProperty')"
    >
      <input
        v-model="model.id"
        type="hidden"
      >
      <b-form-group
        :label="$tk('ClientPortal.Form.Name') + ' *'"
        :invalid-feedback="model.errors['Name']"
        :state="model.errors['Name'] == null ? null : false"
      >
        <b-form-input
          v-model="model.name"
          type="text"
          :state="model.errors['Name'] == null ? null : false"
          :placeholder="$tk('ClientPortal.Form.NamePlaceholder')"
        />
      </b-form-group>
      <b-form-group
        :label="$tk('ClientPortal.Form.Url') + ' *'"
        :invalid-feedback="$tk('ClientPortal.Form.InvalidURL')"
        :state="isValidURL"
        class="mb-0"
      >
        <b-form-input
          v-model="model.url"
          type="text"
          :state="model.errors['Url'] == null ? null : false"
          :placeholder="$tk('ClientPortal.Form.UrlPlaceholder')"
        />
      </b-form-group>
      <template #modal-footer="{ cancel, close }">
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="saving"
          @click.prevent="cancel()"
        >
          {{ $tk('ClientPortal.Form.Actions.Cancel') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="saving || !isFormComplete"
          @click.prevent="saveProperty(model),close()"
        >
          {{ $tk('ClientPortal.Form.Actions.Save') }}
        </b-button>
      </template>
    </b-modal>
  </section>
</template>

<script>
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import notification from '@/mixins/toastification'
import PropertiesService from '@/api/client/properties.service'
import { AccountAccessManager } from '@/utils/accessManager/accessManager'

export default {
  components: {
    GjIcon,
  },
  mixins: [notification],
  data() {
    return {
      model: {
        errors: [],
      },
      saving: false,
      properties: {},
      currentPage: 1,
      perPage: 10,
      loading: true,
      accountAccessManager: AccountAccessManager,
    }
  },
  computed: {
    totalRows() {
      return this.properties.length
    },
    fromCount() {
      return (this.currentPage - 1) * this.perPage + 1
    },
    toCount() {
      const toCount = this.fromCount + this.perPage - 1
      return Math.min(toCount, this.totalRows)
    },
    isFormComplete() {
      const { name, url } = this.model

      return name && url && this.isValidURL
    },
    isValidURL() {
      const { url } = this.model
      const res = url?.match(/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)/g)
      return (res !== null)
    },
    fields() {
      return [
        {
          key: 'name',
          label: this.$tk('ClientPortal.Property.Name'),
        },
        {
          key: 'id',
          label: 'Property ID',
        },
        {
          key: 'url',
          label: this.$tk('ClientPortal.Property.Url'),
        },
        {
          key: 'status',
          label: this.$tk('ClientPortal.Property.Status'),
        },
      ]
    },
  },
  watch: {
    '$route.params.accountId': 'fetchData',
  },
  async mounted() {
    try {
      const response = await PropertiesService.getProperties(this.$route.params.accountId)
      this.properties = response
    } catch (error) {
      this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
    } finally {
      this.loading = false
    }
  },
  methods: {
    async fetchData() {
      try {
        const response = await PropertiesService.getProperties(this.$route.params.accountId)
        this.properties = response
      } catch (error) {
        this.error(this.$tk('ClientPortal.Form.FailedToLoadData'))
      } finally {
        this.loading = false
      }
    },
    async saveProperty(model) {
      this.saving = true
      try {
        const response = await PropertiesService.createProperty(model, this.$route.params.accountId)
        if (response.saved) {
          this.$forceUpdate()
          this.hidePropertyModal()
          if (response.inserted) {
            model.id = response.id
            model.status = response.status
            this.properties.unshift(model)
            this.addAnimation()
            this.success(this.$tk('ClientPortal.Form.PropertyCreatedSuccessfully'))
          } else {
            const property = this.properties.find(x => x.id === model.id)
            property.name = model.name
            property.url = model.url
            this.success(this.$tk('ClientPortal.Form.PropertyUpdatedSuccessfully'))
          }
        } else { this.model.errors = response.errors }
      } catch (error) {
        this.error(error.message)
      } finally {
        this.saving = false
      }
    },
    addAnimation() {
      const el = document.getElementById('properties-table')
      if (this.properties.length === 1) {
        el.classList.add('table-row-animation')
        setTimeout(() => {
          el.classList.remove('table-row-animation')
        }, 3000)
      } else {
        el.rows[1].classList.add('table-row-animation')
        setTimeout(() => {
          el.rows[1].classList.remove('table-row-animation')
        }, 3000)
      }
    },
    editProperty(item) {
      this.model = JSON.parse(JSON.stringify(item))
      this.model.errors = []
      this.$refs['property-modal'].show()
    },
    hidePropertyModal() {
      this.$refs['property-modal'].hide()
    },
    createProperty() {
      this.model = {}
      this.model.errors = []
      this.$refs['property-modal'].show()
    },
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
</style>
